import { useAuth } from "@web/contexts";
import { useEffect, useRef, useState } from "react";
import { signInWithGoogleCredential } from "@/features/auth/api";

/**
 * Google's official branded "Sign In With Google" button using Google Identity Services (GSI).
 *
 * Follows Google's branding guidelines per:
 * https://developers.google.com/identity/gsi/web/guides/display-button
 */
export function GoogleSignInButton({
  text = "signin_with",
  width = 320,
  onSuccess,
  onError,
}: {
  text?: "signin_with" | "signup_with" | "continue_with";
  width?: number | string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);
  const [renderWidth, setRenderWidth] = useState<number>(resolveWidth(width));
  const initialized = useRef(false);
  const { refreshSession, setSession } = useAuth();

  // Get Google Client ID from runtime env (Cloudflare Worker injection)
  // or Vite env (local dev)
  const googleClientId =
    (typeof window !== "undefined"
      ? (
          window as unknown as {
            __ENV__?: { GOOGLE_CLIENT_ID?: string };
          }
        ).__ENV__?.GOOGLE_CLIENT_ID
      : undefined) ??
    (import.meta as { env?: { VITE_GOOGLE_CLIENT_ID?: string } }).env?.VITE_GOOGLE_CLIENT_ID ??
    "";

  // Load GSI script
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (window.google?.accounts) {
      setScriptReady(true);
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]',
    );
    if (existing) {
      existing.addEventListener("load", () => setScriptReady(true));
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptReady(true);
    document.body.appendChild(script);
  }, []);

  // Measure container width when a percentage/relative width is requested
  useEffect(() => {
    if (width !== "100%") {
      setRenderWidth(resolveWidth(width));
      return;
    }

    const el = buttonRef.current;
    if (!el) return;

    const measure = () => setRenderWidth(el.clientWidth || 320);
    measure();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [width]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scriptReady triggers re-render after GSI script loads
  useEffect(() => {
    if (!buttonRef.current || !window.google?.accounts) return;

    if (!initialized.current) {
      initialized.current = true;

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response) => {
          if (busy) return;
          setBusy(true);
          try {
            const { data, error } = await signInWithGoogleCredential(response.credential);
            if (error) {
              onError?.(error.message ?? "Google sign-in failed");
            } else if (data?.token && data.user) {
              // Hydrate from the sign-in response's own user instead of
              // re-verifying through refreshSession's `/auth/session` call,
              // which can lose a read-after-write race against the session
              // row just created and falsely report it as expired.
              setSession(data.user, data.token);
              onSuccess?.();
            } else {
              await refreshSession();
              onSuccess?.();
            }
          } catch {
            onError?.("Google sign-in failed");
          } finally {
            setBusy(false);
          }
        },
        cancel_on_tap_outside: false,
      });
    }

    window.google.accounts.id.renderButton(buttonRef.current, {
      theme: "outline",
      size: "large",
      text,
      shape: "rectangular",
      logo_alignment: "left",
      width: renderWidth,
    });
  }, [
    googleClientId,
    text,
    onSuccess,
    onError,
    refreshSession,
    setSession,
    busy,
    scriptReady,
    renderWidth,
  ]);

  if (!googleClientId) {
    return (
      <div className="text-text-muted text-xs text-center">Google sign-in is not configured</div>
    );
  }

  return <div ref={buttonRef} className={busy ? "opacity-50 pointer-events-none" : ""} />;
}

function resolveWidth(width: number | string): number {
  if (typeof width === "number") return width;
  const parsed = Number.parseInt(width, 10);
  return Number.isNaN(parsed) ? 320 : parsed;
}
